import { useState, useEffect } from "react";
import { Download, Share, SquarePlus, Monitor, Smartphone, Apple, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useInstallAvailable, promptInstall } from "@/lib/pwaInstall";
const FROM_SHARE = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("edition");

const DISMISS_KEY = "appresse_install_seen";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
};

const Step = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-xl border border-primary bg-primary/5 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 font-semibold text-sm">
      <Icon className="w-4 h-4 text-primary" /> {title}
    </div>
    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{children}</p>
  </div>
);

const Instructions = ({ platform, canInstall }) => {
  if (platform === "ios") return (
    <Step icon={Apple} title="iPhone / iPad (Safari)" testid="install-steps-ios">
      Appuyez sur l'icône <Share className="inline w-3.5 h-3.5 mx-0.5" /> <b>Partager</b> en bas de Safari,
      puis choisissez <b>« Sur l'écran d'accueil »</b> <SquarePlus className="inline w-3.5 h-3.5 mx-0.5" />, et validez avec <b>Ajouter</b>.
    </Step>
  );
  if (platform === "android") return (
    <Step icon={Smartphone} title="Android (Chrome)" testid="install-steps-android">
      {canInstall
        ? <>Appuyez sur <b>« Installer l'application maintenant »</b> ci-dessus. Sinon, menu <b>⋮</b> → <b>« Ajouter à l'écran d'accueil »</b>.</>
        : <>Ouvrez le menu <b>⋮</b> de Chrome (en haut à droite) puis <b>« Ajouter à l'écran d'accueil »</b> ou <b>« Installer l'application »</b>.</>}
    </Step>
  );
  return (
    <Step icon={Monitor} title="Ordinateur (Chrome / Edge)" testid="install-steps-desktop">
      {canInstall
        ? <>Cliquez sur <b>« Installer l'application maintenant »</b> ci-dessus, ou sur l'icône d'installation <SquarePlus className="inline w-3.5 h-3.5 mx-0.5" /> dans la barre d'adresse.</>
        : <>Cliquez sur l'icône d'installation <SquarePlus className="inline w-3.5 h-3.5 mx-0.5" /> à droite de la barre d'adresse, ou menu <b>⋮</b> → <b>« Installer APP PRESSE »</b>.</>}
    </Step>
  );
};

export const InstallPrompt = () => {
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [failed, setFailed] = useState(false);
  const canInstall = useInstallAvailable();
  const platform = detectPlatform();

  useEffect(() => {
    if (isStandalone()) return; // already installed
    const onInstalled = () => { setInstalled(true); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);
    // Show once on first visit (only on Home to avoid covering forms/reader)
    const seen = localStorage.getItem(DISMISS_KEY);
    const t = setTimeout(() => {
      if (!seen && window.location.pathname === "/" && !isStandalone() && !FROM_SHARE) setOpen(true);
    }, 1200);
    const openOnDemand = () => { setFailed(false); setOpen(true); };
    window.addEventListener("appresse:open-install", openOnDemand);
    return () => {
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("appresse:open-install", openOnDemand);
      clearTimeout(t);
    };
  }, []);

  const close = () => { localStorage.setItem(DISMISS_KEY, "1"); setOpen(false); };

  const install = async () => {
    const res = await promptInstall();
    if (res === "accepted") { setInstalled(true); localStorage.setItem(DISMISS_KEY, "1"); return; }
    if (res === "failed") setFailed(true);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        className="max-w-md w-[calc(100vw-1.5rem)] max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-2xl p-5 sm:p-6"
        data-testid="install-prompt-dialog">
        <DialogHeader>
          <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/10 mx-auto mb-1">
            <img src="/icon-192.png" alt="APP PRESSE" className="w-9 h-9 rounded-xl" />
          </div>
          <DialogTitle className="text-center font-serif font-black text-xl sm:text-2xl">Installez APP PRESSE</DialogTitle>
          <DialogDescription className="text-center">
            Ajoutez l'icône sur votre écran pour ouvrir votre kiosque en un geste, comme une vraie application.
          </DialogDescription>
        </DialogHeader>

        {installed ? (
          <div className="flex items-center gap-2 justify-center text-emerald-600 py-4" data-testid="install-done">
            <CheckCircle2 className="w-5 h-5" /> Application installée !
          </div>
        ) : (
          <div className="space-y-3">
            {canInstall && platform !== "ios" && (
              <Button onClick={install} className="w-full gap-2 h-11" data-testid="install-now-button">
                <Download className="w-5 h-5" /> Installer l'application maintenant
              </Button>
            )}
            {failed && (
              <div className="flex gap-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm" data-testid="install-failed-message">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Le navigateur n'a pas pu lancer l'installation. Ouvrez le menu <b>⋮</b> de Chrome puis
                  <b> « Ajouter à l'écran d'accueil »</b>.</span>
              </div>
            )}
            <Instructions platform={platform} canInstall={canInstall} />
          </div>
        )}
        <Button variant="ghost" onClick={close} className="w-full gap-2 text-muted-foreground" data-testid="install-later-button">
          <X className="w-4 h-4" /> {installed ? "Fermer" : "Plus tard"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
